import { useCallback } from "react";
import useCrmApi from "../../../components/hook/crm-req";

// Real, live "Bot lead Google Sheets" sync API — confirmed against
// api.crm.dalmlabs.com/openapi.json (useCrmApi's own base URL already ends
// in /api/v1, so these endpoints below omit that prefix). Syncs this
// tenant's bot leads into a Google Sheet the backend creates in the
// connected Google account's own Drive — the account only ever has to grant
// access to files this app creates (the Drive "file" scope), not full Drive
// access.
//
// The `/callback` leg (Google redirecting back with `code`/`state`) is hit
// directly by Google, never by this frontend — the backend itself decides
// whether to redirect the browser onward or just return JSON there, so
// there's no "callback" call to make from here.

export interface GoogleSheetsStatus {
  status: string;
  sheet_url: string | null;
  sync_enabled: boolean;
  last_sync_at: string | null;
  last_error: string | null;
}

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return { Authorization: `Bearer ${token}` };
}

// GET /integrations/google-sheets/bot-leads — any authenticated tenant user.
export function useGoogleSheetsStatus() {
  const { loading, makeRequest } = useCrmApi("/integrations/google-sheets/bot-leads", "GET", authHeaders());
  const get = useCallback(async (): Promise<GoogleSheetsStatus | null> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 ? json : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);
  return { loading, get };
}

// POST /integrations/google-sheets/bot-leads/connect — tenant admin only.
export function useConnectGoogleSheets() {
  const { loading, makeRequest } = useCrmApi("/integrations/google-sheets/bot-leads/connect", "POST", authHeaders());
  const connect = useCallback(async (): Promise<{ authorization_url: string } | null> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 && json?.authorization_url ? json : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);
  return { loading, connect };
}

// DELETE /integrations/google-sheets/bot-leads — tenant admin only.
export function useDisconnectGoogleSheets() {
  const { loading, makeRequest } = useCrmApi("/integrations/google-sheets/bot-leads", "DELETE", authHeaders());
  const disconnect = useCallback(async (): Promise<boolean> => {
    const [, status] = await makeRequest();
    return status >= 200 && status < 300;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);
  return { loading, disconnect };
}

// POST /integrations/google-sheets/bot-leads/retry — tenant admin only.
export function useRetryGoogleSheets() {
  const { loading, makeRequest } = useCrmApi("/integrations/google-sheets/bot-leads/retry", "POST", authHeaders());
  const retry = useCallback(async (): Promise<{ queued: number } | null> => {
    const [json, status] = await makeRequest();
    return status >= 200 && status < 300 ? json : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeRequest]);
  return { loading, retry };
}
